import { useCallback, useEffect } from 'react';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  portfolioIdAtom,
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
} from '~/atoms/dashboardAtom';
import { toastAtom } from '~/atoms/toastAtom';
import { analysisStateAtom, aumDialogVisibleAtom } from '~/atoms/analysisAtom';
import {
  UPDATE_PORTFOLIO,
  UPDATE_PORTFOLIO_SNAPSHOT,
  GET_PORTFOLIO_NAME,
  withUpdatedAt,
} from '~/graphql/queries/portfolio';
import { endpointPortfolioReAnalysisPOST } from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import { useSnapshotVersionCheck } from './useSnapshotVersionCheck';
import { useSnapshotPolling } from './useSnapshotPolling';

export interface UsePortfolioAnalysisOptions {
  snapshotId?: string;
  onAnalysisComplete?: () => void;
  onAnalysisFailure?: (message: string) => void;
  onAnalysisTimeout?: () => void;
}

export const usePortfolioAnalysis = (
  options: UsePortfolioAnalysisOptions = {},
) => {
  const {
    snapshotId: propsSnapshotId,
    onAnalysisComplete,
    onAnalysisFailure,
    onAnalysisTimeout,
  } = options;

  const apolloClient = useApolloClient();
  const portfolioId = useAtomValue(portfolioIdAtom);
  const latestSnapshotIdVal = useAtomValue(latestSnapshotIdAtom);
  const setLatestSnapshotId = useSetAtom(latestSnapshotIdAtom);
  const setTriggerHardRefetch = useSetAtom(triggerHardRefetchAtom);
  const toastCurrent = useAtomValue(toastAtom);

  // Shared global state - all components see the same analysis state
  const [analysisState, setAnalysisState] = useAtom(analysisStateAtom);
  const [aumDialogVisible, setAumDialogVisible] = useAtom(aumDialogVisibleAtom);

  // Use provided snapshotId or fall back to latest
  const effectiveSnapshotId = propsSnapshotId || latestSnapshotIdVal;

  // Portfolio query for AUM data
  // Apollo automatically refetches when variables change with cache-and-network policy
  const { data: portfolioData, refetch: refetchPortfolio } = useQuery(
    GET_PORTFOLIO_NAME,
    {
      variables: { portfolioId },
      skip: !portfolioId,
      fetchPolicy: 'cache-and-network',
    },
  );

  // Update portfolio mutation
  const [updatePortfolio, { loading: updatePortfolioLoading }] = useMutation(
    UPDATE_PORTFOLIO,
    {
      refetchQueries: [GET_PORTFOLIO_NAME],
    },
  );

  // Update snapshot mutation
  const [updateSnapshot, { loading: updateSnapshotLoading }] = useMutation(
    UPDATE_PORTFOLIO_SNAPSHOT,
  );

  // Snapshot version check
  const { checkAndUpdateBeforeAnalysis } = useSnapshotVersionCheck();

  // Polling hook - single source of truth for polling
  const {
    isAnalyzing: pollingIsAnalyzing,
    progress: pollingProgress,
    status: pollingStatus,
    statusValue: pollingStatusValue,
    startPolling: startPollingForSnapshot,
    resetState: resetPollingState,
    setAnalyzingState: setPollingAnalyzingState,
  } = useSnapshotPolling(effectiveSnapshotId);

  // Reset analysis state when snapshot changes (switching portfolios)
  useEffect(() => {
    setAnalysisState({
      isAnalyzing: false,
      progress: 0,
      status: '',
      statusValue: '',
    });
  }, [effectiveSnapshotId, setAnalysisState]);

  // Sync polling state to global atom whenever it changes
  // This ensures all components see the same state even if they mount at different times
  useEffect(() => {
    setAnalysisState({
      isAnalyzing: pollingIsAnalyzing,
      progress: pollingProgress,
      status: pollingStatus,
      statusValue: pollingStatusValue,
    });
  }, [
    pollingIsAnalyzing,
    pollingProgress,
    pollingStatus,
    pollingStatusValue,
    setAnalysisState,
  ]);

  // Watch for analysis completion (even after page refresh)
  // This ensures cache is cleared and refetch is triggered regardless of how we detected completion
  useEffect(() => {
    if (pollingStatusValue === 'completed' && pollingProgress === 100) {
      console.log(
        '[usePortfolioAnalysis] Detected analysis completion, clearing cache and triggering refetch',
      );

      // Clear Apollo cache for analysis queries to force fresh data
      apolloClient.cache.evict({ fieldName: 'EQPortfoliosAIAnalysis' });
      apolloClient.cache.evict({ fieldName: 'EQPortfoliosAnalysis' });
      apolloClient.cache.evict({ fieldName: 'EQSectorNews' });
      apolloClient.cache.gc(); // Garbage collect evicted entries

      console.log(
        '[usePortfolioAnalysis] Cache cleared, incrementing triggerHardRefetch',
      );

      // Trigger hard refetch for all widgets
      setTriggerHardRefetch((prev) => {
        const newValue = prev + 1;
        console.log(
          '[usePortfolioAnalysis] triggerHardRefetch updated:',
          prev,
          '->',
          newValue,
        );
        return newValue;
      });
    }
  }, [
    pollingStatusValue,
    pollingProgress,
    apolloClient,
    setTriggerHardRefetch,
  ]);

  // Utility function to show toast messages
  const showToast = useCallback(
    (
      severity: 'success' | 'error' | 'warn',
      summary: string,
      detail: string,
      life: number = 3000,
    ) => {
      if (toastCurrent) {
        toastCurrent.show({ severity, summary, detail, life });
      }
    },
    [toastCurrent],
  );

  // Handle re-analysis - shows AUM dialog
  const handleReAnalyse = useCallback(() => {
    if (!effectiveSnapshotId) {
      showToast(
        'warn',
        'No Snapshot ID',
        'Cannot analyze without a snapshot ID. Please upload a snapshot first.',
      );
      return;
    }

    // Show AUM confirmation dialog first
    setAumDialogVisible(true);
  }, [effectiveSnapshotId, showToast]);

  // Handle AUM confirmation and proceed with analysis
  const handleAUMConfirm = useCallback(
    async (aum: number, refetchCallback?: () => void) => {
      setAumDialogVisible(false);

      // Update portfolio AUM if it changed
      const currentPortfolioAUM = portfolioData?.EQPortfolio?.[0]?.aum;
      try {
        if (aum !== currentPortfolioAUM) {
          await updatePortfolio({
            variables: {
              id: portfolioId,
              changes: withUpdatedAt({ aum }),
            },
          });
        }

        // Always update snapshot AUM to ensure the latest snapshot has the correct value
        if (effectiveSnapshotId) {
          await updateSnapshot({
            variables: {
              snapshotId: effectiveSnapshotId,
              changes: { aum },
            },
          });
        }
        refetchPortfolio();
        refetchCallback?.();
        if (aum !== currentPortfolioAUM) {
          showToast(
            'success',
            'AUM Updated',
            'Portfolio and snapshot AUM have been updated successfully.',
          );
        }
      } catch (error) {
        showToast(
          'warn',
          'AUM Update Failed',
          'Failed to update AUM, but analysis will continue.',
        );
      }

      // Proceed with analysis
      try {
        // Check and update to latest snapshot before analysis
        const updatedSnapshotId = await checkAndUpdateBeforeAnalysis();

        // If no snapshot ID returned, it means there's a newer snapshot available
        if (!updatedSnapshotId) {
          showToast(
            'warn',
            'Outdated Snapshot',
            'A newer snapshot is available. Please refresh to the latest snapshot before running analysis.',
          );
          return;
        }

        if (!portfolioId) {
          showToast(
            'error',
            'Analysis Failed',
            'No portfolio ID available for analysis.',
          );
          return;
        }

        // Capture the exact timestamp when user clicked "Analyse"
        const analysisStartTime = Date.now();

        // Show status bar immediately at 0% (without starting polling yet)
        setPollingAnalyzingState();

        // Trigger hard refetch for all other widgets
        setTriggerHardRefetch((prev) => prev + 1);

        // Submit the analysis request
        endpointPortfolioReAnalysisPOST({
          portfolio_id: portfolioId,
          snapshot_id: updatedSnapshotId,
          force_recalculation: true,
        })
          .then((response) => {
            if (response && response.snapshot_id) {
              setLatestSnapshotId(response.snapshot_id);
            }

            // NOW start polling after API call completes
            // Pass the analysis start time so polling knows when the 30s grace period started
            startPollingForSnapshot({
              analysisStartTime,
              onComplete: () => {
                console.log(
                  '[usePortfolioAnalysis] Analysis complete, clearing cache and triggering refetch',
                );

                // Clear Apollo cache for analysis queries to force fresh data
                apolloClient.cache.evict({
                  fieldName: 'EQPortfoliosAIAnalysis',
                });
                apolloClient.cache.evict({ fieldName: 'EQPortfoliosAnalysis' });
                apolloClient.cache.evict({ fieldName: 'EQSectorNews' });
                apolloClient.cache.gc(); // Garbage collect evicted entries

                console.log(
                  '[usePortfolioAnalysis] Cache cleared, incrementing triggerHardRefetch',
                );

                // Trigger hard refetch for all other widgets when re-analysis completes
                setTriggerHardRefetch((prev) => {
                  const newValue = prev + 1;
                  console.log(
                    '[usePortfolioAnalysis] triggerHardRefetch updated:',
                    prev,
                    '->',
                    newValue,
                  );
                  return newValue;
                });
                showToast(
                  'success',
                  'Analysis Complete',
                  'Portfolio analysis completed successfully!',
                  5000,
                );
                refetchCallback?.();
                onAnalysisComplete?.();
                // Don't reset state - let user dismiss the completed progress bar
              },
              onFailure: (message: string) => {
                showToast(
                  'error',
                  'Analysis Failed',
                  `Analysis failed: ${message}`,
                  5000,
                );
                onAnalysisFailure?.(message);
                // Don't reset state - let user dismiss the error message
              },
              onTimeout: () => {
                showToast(
                  'warn',
                  'Analysis Timeout',
                  'Analysis is taking longer than expected. Please check back later.',
                  5000,
                );
                onAnalysisTimeout?.();
                // Don't reset state - let user dismiss the timeout message
              },
            });
          })
          .catch((_error) => {
            showToast(
              'error',
              'Analysis Failed',
              'Failed to submit portfolio analysis. Please try again.',
            );
            resetPollingState();
          });
      } catch (error) {
        showToast(
          'error',
          'Analysis Failed',
          'Failed to submit portfolio analysis. Please try again.',
        );
        resetPollingState();
      }
    },
    [
      setAumDialogVisible,
      portfolioData?.EQPortfolio,
      updatePortfolio,
      portfolioId,
      effectiveSnapshotId,
      refetchPortfolio,
      showToast,
      updateSnapshot,
      checkAndUpdateBeforeAnalysis,
      setPollingAnalyzingState,
      setTriggerHardRefetch,
      startPollingForSnapshot,
      setLatestSnapshotId,
      apolloClient.cache,
      onAnalysisComplete,
      onAnalysisFailure,
      onAnalysisTimeout,
      resetPollingState,
    ],
  );

  // Wrapper for reset to sync both polling state and atom
  const resetAnalysisState = useCallback(() => {
    resetPollingState();
    setAnalysisState({
      isAnalyzing: false,
      progress: 0,
      status: '',
      statusValue: '',
    });
  }, [resetPollingState, setAnalysisState]);

  return {
    // State - from shared atom so all components see the same values
    isAnalyzing: analysisState?.isAnalyzing ?? false,
    analysisProgress: analysisState?.progress ?? 0,
    analysisStatus: analysisState?.status ?? '',
    analysisStatusValue: analysisState?.statusValue ?? '',
    aumDialogVisible,

    // Data
    portfolioData,
    currentAUM: portfolioData?.EQPortfolio?.[0]?.aum || 0,
    currency: portfolioData?.EQPortfolio?.[0]?.base_currency,

    // Loading states
    isUpdatingAUM: updatePortfolioLoading || updateSnapshotLoading,

    // Actions
    handleReAnalyse,
    handleAUMConfirm,
    setAumDialogVisible,
    resetAnalysisState,
    showToast,
  };
};
